import { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import React, { useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import { messages } from './messages';
import { useListContext } from './useListContext';

interface ListItemPositionDropdownProps<T> {
  position: ItemPosition;
  index: number;
  item: T;
  onReorder?: (from: number, to: number) => void;
  setListState: React.Dispatch<React.SetStateAction<ListState<T>>>;
}

type ItemPosition = 'first' | 'last' | 'middle' | 'only';

type ListState<T> = {
  items: T[];
  lastCardMoved: {
    item: T;
    dragHandleRef?: React.RefObject<HTMLButtonElement>;
  } | null;
};

export default function ListItemPositionDropdown<T>({
  position,
  index,
  item,
  onReorder,
  setListState,
}: ListItemPositionDropdownProps<T>) {
  const { formatMessage } = useIntl();
  const { getListLength } = useListContext();

  const isMoveUpDisabled = position === 'first' || position === 'only';
  const isMoveDownDisabled = position === 'last' || position === 'only';

  const moveToTop = useCallback(() => {
    onReorder?.(index, 0);
    setListState((state) => ({
      ...state,
      lastCardMoved: {
        item,
      },
    }));
  }, [index, item, onReorder, setListState]);

  const moveUp = useCallback(() => {
    onReorder?.(index, index - 1);
    setListState((state) => ({
      ...state,
      lastCardMoved: {
        item,
      },
    }));
  }, [index, item, onReorder, setListState]);

  const moveDown = useCallback(() => {
    onReorder?.(index, index + 1);
    setListState((state) => ({
      ...state,
      lastCardMoved: {
        item,
      },
    }));
  }, [index, item, onReorder, setListState]);

  const moveToBottom = useCallback(() => {
    onReorder?.(index, getListLength() - 1);
    setListState((state) => ({
      ...state,
      lastCardMoved: {
        item,
      },
    }));
  }, [onReorder, index, getListLength, setListState, item]);

  return (
    <DropdownItemGroup>
      <DropdownItem onClick={moveToTop} isDisabled={isMoveUpDisabled}>
        {formatMessage(messages['draggable-list.dropdown-menu-move-to-top'])}
      </DropdownItem>
      <DropdownItem onClick={moveUp} isDisabled={isMoveUpDisabled}>
        {formatMessage(messages['draggable-list.dropdown-menu-move-up'])}
      </DropdownItem>
      <DropdownItem onClick={moveDown} isDisabled={isMoveDownDisabled}>
        {formatMessage(messages['draggable-list.dropdown-menu-move-down'])}
      </DropdownItem>
      <DropdownItem onClick={moveToBottom} isDisabled={isMoveDownDisabled}>
        {formatMessage(messages['draggable-list.dropdown-menu-move-to-bottom'])}
      </DropdownItem>
    </DropdownItemGroup>
  );
}
